<template>
  <div class="set-confirm-step pa-4">
    <!-- セット開始時間: bulk のときだけ共有ピッカー、individual のときは行ごと -->
    <div
      v-if="!isIndividual"
      class="d-flex align-center mb-4 start-at-row"
    >
      <div class="text-h6 font-weight-bold flex-grow-1">
        セット開始時間
      </div>
      <v-text-field
        :value="startTimeText"
        type="time"
        step="300"
        hide-details
        outlined
        class="start-at-input"
        style="max-width: 200px;"
        @input="onStartTimeChange"
      />
    </div>

    <div class="d-flex justify-space-between align-center mb-4">
      <v-card-subtitle class="pa-0 text-h6 font-weight-bold">
        料金の確認
      </v-card-subtitle>
      <v-btn
        large
        text
        color="primary"
        @click="toggleMode"
      >
        {{ isIndividual ? 'まとめて設定する' : '個別に設定する' }}
      </v-btn>
    </div>

    <!-- bulk モード -->
    <div
      v-if="!isIndividual"
      class="d-flex align-center py-3 set-bulk-row"
    >
      <div class="flex-grow-1 text-body-1 font-weight-medium">
        {{ customerCount }}名（まとめ）
      </div>
      <v-text-field
        :value="set.price"
        type="number"
        hide-details
        outlined
        style="max-width: 140px;"
        class="set-input"
        @input="updateBulkPrice"
      />
      <span class="mx-2 text-body-1">円</span>
      <v-btn-toggle
        :value="taxCharge || 'normal'"
        mandatory
        class="ml-3"
        @change="updateBulkTaxCharge"
      >
        <v-btn
          large
          value="normal"
        >
          通常
        </v-btn>
        <v-btn
          large
          value="cut"
        >
          カット
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- individual モード -->
    <div v-else>
      <set-editable-row
        v-for="(row, idx) in set.perCustomer"
        :key="`set-row-${idx}`"
        :label="`お客様${idx + 1}`"
        :price="row.price"
        :tax-charge="row.taxCharge || 'normal'"
        :start-at="row.startAt || startAt"
        @update:price="updatePerCustomer(idx, { price: $event })"
        @update:taxCharge="updatePerCustomer(idx, { taxCharge: $event })"
        @update:startAt="updatePerCustomer(idx, { startAt: $event })"
      />
    </div>

    <v-divider class="my-4" />

    <div class="d-flex flex-column align-end">
      <div class="text-h5 font-weight-bold">
        セット小計: {{ formatPrice(subtotal) }} 円
      </div>
      <div
        v-if="cutCount > 0"
        class="text-body-2 text--secondary mt-1"
      >
        ※ {{ cutCount }} 件は税サカット（{{ formatPrice(subtotal) }} 円に税サは加算されません）
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import SetEditableRow from '../parts/SetEditableRow.vue'

const buildPerCustomer = (count, base) => {
  const arr = []
  for (let i = 0; i < count; i += 1) {
    arr.push({
      price: base.price,
      taxCharge: base.taxCharge || 'normal',
      startAt: base.startAt instanceof Date ? new Date(base.startAt.getTime()) : null,
    })
  }

  return arr
}

const pad2 = n => String(n).padStart(2, '0')

export default {
  name: 'SetConfirmStep',
  components: { SetEditableRow },
  props: {
    customerCount: { type: Number, required: true },
    selectedCourse: { type: Object, default: null },
    set: { type: Object, required: true },
    taxCharge: { type: String, default: 'normal' },
    startAt: { type: Date, default: null },
  },
  emits: ['update:set', 'update:taxCharge', 'update:startAt'],
  setup(props, { emit }) {
    const isIndividual = computed(() => props.set.mode === 'individual')

    // セット小計: 既存仕様と同じく selling_price は taxCharge に関係なく合計に含める。
    // taxCharge: 'cut' は税サ（税+サービス料）が加算されないという意味で、本体価格は計上される。
    const subtotal = computed(() => {
      if (isIndividual.value) {
        return (props.set.perCustomer || [])
          .reduce((sum, r) => sum + Number(r.price || 0), 0)
      }

      return Number(props.set.price || 0) * Math.max(1, props.customerCount)
    })

    const cutCount = computed(() => {
      if (isIndividual.value) {
        return (props.set.perCustomer || [])
          .filter(r => (r.taxCharge || 'normal') === 'cut').length
      }

      return (props.taxCharge || 'normal') === 'cut'
        ? Math.max(1, props.customerCount)
        : 0
    })

    const formatPrice = n => Number(n || 0).toLocaleString('ja-JP')

    const emitSet = next => emit('update:set', next)

    const toggleMode = () => {
      if (isIndividual.value) {
        emitSet({ ...props.set, mode: 'bulk' })
      } else {
        emitSet({
          ...props.set,
          mode: 'individual',
          perCustomer: buildPerCustomer(props.customerCount, {
            price: props.set.price,
            taxCharge: props.taxCharge || 'normal',
            startAt: props.startAt,
          }),
        })
      }
    }

    const updateBulkPrice = v => emitSet({ ...props.set, price: Number(v) || 0 })
    const updateBulkTaxCharge = v => emit('update:taxCharge', v || 'normal')

    const updatePerCustomer = (idx, patch) => {
      const next = (props.set.perCustomer || []).map((r, i) => (
        i === idx ? { ...r, ...patch } : r
      ))
      emitSet({ ...props.set, perCustomer: next })
    }

    // 開始時刻 (Date) ↔ "HH:mm" 文字列の相互変換
    const startTimeText = computed(() => {
      const d = props.startAt
      if (!(d instanceof Date)) return ''

      return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    })

    const onStartTimeChange = v => {
      if (!v) return
      const [h, m] = v.split(':').map(Number)
      if (Number.isNaN(h) || Number.isNaN(m)) return
      const base = props.startAt instanceof Date ? new Date(props.startAt.getTime()) : new Date()
      base.setHours(h, m, 0, 0)
      emit('update:startAt', base)
    }

    return {
      isIndividual,
      subtotal,
      cutCount,
      formatPrice,
      toggleMode,
      updateBulkPrice,
      updateBulkTaxCharge,
      updatePerCustomer,
      startTimeText,
      onStartTimeChange,
    }
  },
}
</script>

<style scoped>
.set-bulk-row {
  border-bottom: 1px solid rgba(127, 127, 127, 0.12);
}
.set-input :deep(input) {
  font-size: 1.1rem;
  text-align: right;
}
</style>

